import { Injectable, signal } from '@angular/core';
import { Form } from '../../../core/models/form.model';
import { CheckPointService } from '../../../core/services/checkPoint.service';
import { FormService } from '../../../core/services/form.service';
import { LookupListService } from '../../../core/services/lookup-list.service';
import { EntityConfig, EntityType, SelectorOptions } from './entity-list-selector.types';

/**
 * Service for controlling the global entity list selector dialog
 * and managing entity-specific configurations.
 */
@Injectable({ providedIn: 'root' })
export class EntityListSelectorService {
  constructor(
    private formService: FormService,
    private checkPointService: CheckPointService,
    private lookupListService: LookupListService,
  ) {}

  /** Whether the dialog is currently open */
  isOpen = signal(false);

  /** Current entity type being selected */
  currentEntityType = signal<EntityType | null>(null);

  /** Current selector configuration */
  config = signal<SelectorOptions | null>(null);

  /** Promise resolver for returning selected items */
  private resolvePromise: ((value: any[]) => void) | null = null;

  /**
   * Get entity-specific configuration based on entity type
   */
  getEntityConfig(entityType: EntityType): EntityConfig {
    switch (entityType) {
      case 'forms':
        return {
          displayColumns: ['name', 'description'],
          searchFields: ['name', 'description'],
          idField: 'id',
          labelField: 'name',
          descriptionField: 'description',
          service: this.formService,
        };
      case 'checkPoints':
        return {
          displayColumns: ['name', 'description'],
          searchFields: ['name', 'description'],
          idField: 'id',
          labelField: 'name',
          descriptionField: 'description',
          service: this.checkPointService,
        };
      case 'users':
        // TODO: Implement when UserService is available
        throw new Error('User selection not yet implemented');
      case 'locations':
        // TODO: Implement when LocationService is available
        throw new Error('Location selection not yet implemented');
      case 'lookup-lists':
        return {
          displayColumns: ['name'],
          searchFields: ['name'],
          idField: 'id',
          labelField: 'name',
          inactiveField: 'is_active',
          descriptionFn: (list: any) =>
            `${list.options.length} option${list.options.length === 1 ? '' : 's'}`,
          service: this.lookupListService,
        };
      default:
        throw new Error(`Unsupported entity type: ${entityType}`);
    }
  }

  /**
   * Open the entity selector dialog (generic method)
   * @template T The entity type being selected
   * @param entityType Which entity type to select
   * @param options Selector configuration options
   * @returns Promise that resolves with selected items
   */
  openSelector<T>(entityType: EntityType, options: SelectorOptions): Promise<T[]> {
    this.currentEntityType.set(entityType);
    this.config.set(options);
    this.isOpen.set(true);

    return new Promise<T[]>((resolve) => {
      this.resolvePromise = resolve;
    });
  }

  /**
   * Type-safe convenience method for selecting forms/forms
   * @param options Selector configuration options
   * @returns Promise that resolves with selected forms
   */
  selectForms(options: SelectorOptions): Promise<Form[]> {
    return this.openSelector<Form>('forms', options);
  }

  /**
   * Called by dialog when user confirms selection
   * @param selected The selected items
   */
  confirm(selected: any[]): void {
    if (this.resolvePromise) {
      this.resolvePromise(selected);
      this.resolvePromise = null;
    }
    this.close();
  }

  /**
   * Called by dialog when user cancels
   */
  cancel(): void {
    if (this.resolvePromise) {
      this.resolvePromise([]);
      this.resolvePromise = null;
    }
    this.close();
  }

  /**
   * Close the dialog and reset state
   */
  private close(): void {
    this.isOpen.set(false);
    this.currentEntityType.set(null);
    this.config.set(null);
  }
}
